// Histórico de chamadas (/call-historicals) — ligações de voz via Wavoip.
// Mockup: "Aeros Dark — Histórico de chamadas".
//
// Anotados para backend futuro (não existem hoje): KPI de duração média e o
// filtro "Período: 7 dias" do mockup, além da coluna Ticket — a API da Wavoip
// não devolve o ticket da chamada. O lugar do Ticket é ocupado pelas ações
// que já funcionam: baixar gravação e ligar de volta.
import React, { useContext, useEffect, useReducer, useState } from "react";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import Tooltip from "@material-ui/core/Tooltip";
import SearchIcon from "@material-ui/icons/Search";
import PhoneIcon from "@material-ui/icons/Phone";
import DownloadIcon from "@material-ui/icons/CloudDownload";
import CallMadeIcon from "@material-ui/icons/CallMade";
import CallReceivedIcon from "@material-ui/icons/CallReceived";
import CallIcon from "@material-ui/icons/Call";
import CallEndIcon from "@material-ui/icons/CallEnd";
import PhoneMissedIcon from "@material-ui/icons/PhoneMissed";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import { makeStyles, alpha, useTheme } from "@material-ui/core/styles";
import { isArray } from "lodash";

import MainContainer from "../../components/MainContainer";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import useCampaignPageStyles from "../Campaigns/campaignPageStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import {
  statusInfo,
  tipoInfo,
  formataDuracao,
  formataTelefone,
  formataDataHora,
  iniciais,
  nomeChamada,
  kpisChamadas,
} from "./callRules";

const GRID =
  "minmax(0,1.5fr) 150px 130px 140px 100px 130px minmax(0,1fr) 96px";

const KPI_ICON = {
  total: CallIcon,
  served: CallEndIcon,
  rejected: PhoneMissedIcon,
  finished: DoneAllIcon,
};

const reducer = (state, action) => {
  if (action.type === "LOAD") {
    const chegando = isArray(action.payload) ? action.payload : [];
    const novas = [];
    chegando.forEach((call) => {
      const i = state.findIndex((c) => c.id === call.id);
      if (i !== -1) state[i] = call;
      else novas.push(call);
    });
    return [...state, ...novas];
  }
  if (action.type === "UPSERT") {
    const call = action.payload;
    const i = state.findIndex((c) => c.id === call.id);
    if (i !== -1) {
      state[i] = call;
      return [...state];
    }
    return [call, ...state];
  }
  if (action.type === "RESET") return [];
  return state;
};

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    headerSubtitle: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginTop: 2,
    },
    kpiRow: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0,1fr))",
      gap: 12,
      marginBottom: 16,
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "repeat(2, minmax(0,1fr))",
      },
    },
    kpi: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 12,
      padding: "14px 16px",
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    kpiIcon: {
      width: 36,
      height: 36,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      "& .MuiSvgIcon-root": { fontSize: 19 },
    },
    kpiValue: {
      fontSize: 20,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
    },
    kpiLabel: { fontSize: 11.5, color: theme.palette.text.secondary },
    card: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      overflow: "hidden",
    },
    rowHead: {
      display: "grid",
      gridTemplateColumns: GRID,
      gap: 10,
      padding: "10px 18px",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      borderBottom: `1px solid ${border}`,
    },
    row: {
      display: "grid",
      gridTemplateColumns: GRID,
      gap: 10,
      padding: "11px 18px",
      borderBottom: `1px solid ${
        isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"
      }`,
      alignItems: "center",
      fontSize: 13,
    },
    pessoa: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
    avatar: {
      width: 30,
      height: 30,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      fontSize: 11,
      fontWeight: 700,
      color: isLight ? "#FFFFFF" : "#07080D",
    },
    nome: {
      fontWeight: 600,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    mono: {
      color: theme.palette.text.secondary,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    monoSmall: { fontSize: 12 },
    statusPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 99,
      padding: "3px 9px",
      whiteSpace: "nowrap",
    },
    statusDot: { width: 6, height: 6, borderRadius: "50%", flex: "none" },
    duracao: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
    tipo: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      fontSize: 11.5,
      fontWeight: 600,
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },
    atendente: {
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    acoes: { display: "flex", gap: 2, justifyContent: "flex-end" },
    footerRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "12px 18px",
    },
    loadMore: {
      border: `1px solid ${
        isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.14)"
      }`,
      borderRadius: 9,
      padding: "7px 16px",
      fontSize: 12.5,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      textTransform: "none",
      backgroundColor: "transparent",
      cursor: "pointer",
      "&:hover": { color: theme.palette.text.primary },
    },
    vazio: {
      padding: "34px 18px",
      textAlign: "center",
      fontSize: 12.5,
      color: theme.palette.aeros.textTertiary,
    },
    erroBox: {
      padding: "34px 18px",
      textAlign: "center",
      "& .MuiSvgIcon-root": { fontSize: 30, color: AEROS.warning },
    },
    erroTitle: {
      marginTop: 8,
      fontSize: 14,
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    erroText: {
      marginTop: 4,
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      maxWidth: 460,
      marginLeft: "auto",
      marginRight: "auto",
    },
  };
});

const CallHistoricals = () => {
  const c = useCampaignPageStyles();
  const classes = useStyles();
  const theme = useTheme();
  const { user, socket } = useContext(AuthContext);

  const [chamadas, dispatch] = useReducer(reducer, []);
  const [termo, setTermo] = useState("");
  const [pagina, setPagina] = useState(1);
  const [temMais, setTemMais] = useState(false);
  const [carregando, setCarregando] = useState(true);
  // A Wavoip pode não estar configurada (token ausente na conexão). Nesse
  // caso a tela explica o que fazer em vez de despejar o erro cru da API,
  // que era o que a versão anterior fazia via toast.
  const [indisponivel, setIndisponivel] = useState(false);
  const [stats, setStats] = useState({});

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPagina(1);
  }, [termo]);

  useEffect(() => {
    setCarregando(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/call/historical", {
          params: { searchParam: termo, pageNumber: pagina },
        });
        dispatch({ type: "LOAD", payload: data?.historical?.resultFinal });
        setStats(data?.historical || {});
        setTemMais(Boolean(data?.hasMore));
        setIndisponivel(false);
      } catch (err) {
        setIndisponivel(true);
      }
      setCarregando(false);
    }, 500);
    return () => clearTimeout(t);
  }, [termo, pagina]);

  useEffect(() => {
    if (!socket) return undefined;
    const canal = `company-${user.companyId}-call-history`;
    const onEvento = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPSERT", payload: data.record });
      }
    };
    socket.on(canal, onEvento);
    return () => socket.off(canal, onEvento);
  }, [socket, user.companyId]);

  const baixarGravacao = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "audio.mp3");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const corDe = (semente) =>
    AEROS.avatarCycle[Math.abs(Number(semente) || 0) % AEROS.avatarCycle.length];

  const kpis = kpisChamadas(stats);

  return (
    <MainContainer>
      <div className={c.pageHeader}>
        <div>
          <div className={c.pageTitle}>{i18n.t("aeros.calls.title")}</div>
          <div className={classes.headerSubtitle}>
            {i18n.t("aeros.calls.subtitle")}
          </div>
        </div>
        <div className={c.headerActions}>
          <InputBase
            className={c.searchPill}
            placeholder={i18n.t("aeros.calls.search")}
            type="search"
            value={termo}
            onChange={(e) => setTermo(e.target.value.toLowerCase())}
            startAdornment={<SearchIcon />}
            inputProps={{ "aria-label": i18n.t("aeros.calls.search") }}
          />
        </div>
      </div>

      <div className={c.pageBody}>
        <div className={classes.kpiRow}>
          {kpis.map((k) => {
            const Icone = KPI_ICON[k.key];
            return (
              <div className={classes.kpi} key={k.key}>
                <div
                  className={classes.kpiIcon}
                  style={{
                    backgroundColor: alpha(k.color, 0.13),
                    color: stateText(theme, k.color),
                  }}
                >
                  <Icone />
                </div>
                <div>
                  <div className={classes.kpiValue}>{k.value}</div>
                  <div className={classes.kpiLabel}>
                    {i18n.t(`aeros.calls.kpi.${k.key}`)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className={classes.card}>
          <div className={classes.rowHead}>
            <div>{i18n.t("aeros.calls.contact")}</div>
            <div>{i18n.t("aeros.calls.phone")}</div>
            <div>{i18n.t("aeros.calls.dateTime")}</div>
            <div>{i18n.t("aeros.calls.statusColumn")}</div>
            <div style={{ textAlign: "right" }}>
              {i18n.t("aeros.calls.duration")}
            </div>
            <div>{i18n.t("aeros.calls.typeColumn")}</div>
            <div>{i18n.t("aeros.calls.agent")}</div>
            <div style={{ textAlign: "right" }}>
              {i18n.t("aeros.calls.actions")}
            </div>
          </div>

          {indisponivel ? (
            <div className={classes.erroBox}>
              <ReportProblemOutlinedIcon />
              <div className={classes.erroTitle}>
                {i18n.t("aeros.calls.unavailableTitle")}
              </div>
              <div className={classes.erroText}>
                {i18n.t("aeros.calls.unavailableText")}
              </div>
            </div>
          ) : carregando && chamadas.length === 0 ? (
            <div className={classes.vazio}>{i18n.t("aeros.calls.loading")}</div>
          ) : chamadas.length === 0 ? (
            <div className={classes.vazio}>
              {termo
                ? i18n.t("aeros.calls.emptySearch")
                : i18n.t("aeros.calls.empty")}
            </div>
          ) : (
            chamadas.map((call) => {
              const st = statusInfo(call.devices?.status);
              const tp = tipoInfo(call.devices?.direction);
              const duracao = formataDuracao(call.devices?.duration);
              const nome = nomeChamada(call);
              return (
                <div className={classes.row} key={call.id || call.devices?.id}>
                  <div className={classes.pessoa}>
                    <div
                      className={classes.avatar}
                      style={{ backgroundColor: corDe(call.id) }}
                    >
                      {iniciais(nome)}
                    </div>
                    <span className={classes.nome} title={nome}>
                      {nome}
                    </span>
                  </div>

                  <div className={classes.mono}>
                    {formataTelefone(call.phone_to)}
                  </div>
                  <div className={`${classes.mono} ${classes.monoSmall}`}>
                    {formataDataHora(
                      call.createdAt || call.devices?.created_date
                    )}
                  </div>

                  <div>
                    <span
                      className={classes.statusPill}
                      style={{
                        color: stateText(theme, st.color, st.state),
                        backgroundColor: alpha(st.color, 0.1),
                      }}
                    >
                      <span
                        className={classes.statusDot}
                        style={{ backgroundColor: st.color }}
                      />
                      {st.label}
                    </span>
                  </div>

                  <div
                    className={classes.duracao}
                    style={{
                      color:
                        duracao === "—"
                          ? theme.palette.aeros.textTertiary
                          : theme.palette.text.primary,
                    }}
                  >
                    {duracao}
                  </div>

                  <div>
                    <span
                      className={classes.tipo}
                      style={{ color: stateText(theme, tp.color) }}
                    >
                      {tp.saida ? <CallMadeIcon /> : <CallReceivedIcon />}
                      {tp.label}
                    </span>
                  </div>

                  <div className={classes.atendente} title={call.user?.name}>
                    {call.user?.name || "—"}
                  </div>

                  <div className={classes.acoes}>
                    {call.callSaveUrl && (
                      <Tooltip title={i18n.t("aeros.calls.download")}>
                        <IconButton
                          className={c.rowActionButton}
                          aria-label={i18n.t("aeros.calls.download")}
                          onClick={() => baixarGravacao(call.callSaveUrl)}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {call.url && (
                      <Tooltip title={i18n.t("aeros.calls.callBack")}>
                        <IconButton
                          className={c.rowActionButton}
                          aria-label={i18n.t("aeros.calls.callBack")}
                          onClick={() => window.open(call.url, "_blank")}
                        >
                          <PhoneIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </div>
              );
            })
          )}

          {temMais && !indisponivel && (
            <div className={classes.footerRow}>
              <button
                type="button"
                className={classes.loadMore}
                onClick={() => setPagina((p) => p + 1)}
                disabled={carregando}
              >
                {i18n.t("aeros.calls.loadMore")}
              </button>
            </div>
          )}
        </div>
      </div>
    </MainContainer>
  );
};

export default CallHistoricals;
